/* eslint-disable max-lines */
import {
  ExtractRouteParams,
  generatePath,
  RouteComponentProps,
} from "react-router";

import { AvailableFeatures } from "@/utils/constants/features";
import {
  ADMINISTRATION_ROUTING,
  FEATURE_TO_ROUTES,
  type PATH_TO_QUERY_PARAMS,
  RouteParameters,
  RouteParametersArray,
  ROUTING,
  SUPPORTED_ROUTES,
} from "@/utils/constants/routing";
import { FeatureAccessLevel } from "@/utils/constants/subscriptions";

const FREE_DASHBOARD_ROUTES = [
  ...FEATURE_TO_ROUTES[AvailableFeatures.ORGANIZATION_MANAGEMENT],
  ...FEATURE_TO_ROUTES[AvailableFeatures.COUNTERS],
  ...FEATURE_TO_ROUTES[AvailableFeatures.RISK],
] as const;

const TIER_1_DASHBOARD_ROUTES = [
    ...FEATURE_TO_ROUTES[AvailableFeatures.ENGINE_TRENDING],
    ...FEATURE_TO_ROUTES[AvailableFeatures.WEIGHT_AND_BALANCE],
    ...FEATURE_TO_ROUTES[AvailableFeatures.PERFORMANCE],
    // ...FEATURE_TO_ROUTES[AvailableFeatures.TRAINING],
    ...FEATURE_TO_ROUTES[AvailableFeatures.MAINTENANCE_INTELLIGENCE],
    ...FEATURE_TO_ROUTES[AvailableFeatures.SQUAWKS],
    // ...FEATURE_TO_ROUTES[AvailableFeatures.BINDER],
    ...FEATURE_TO_ROUTES[AvailableFeatures.REPORTS],
  ] as const,
  // Tier2 is premium+mfrat, we don't have any other routes that are exclusive to tier2
  // so we use the same routes as tier1

  TIER_2_DASHBOARD_ROUTES = [...TIER_1_DASHBOARD_ROUTES] as const,
  ACTIVE_DASHBOARD_ROUTES = [
    ...FREE_DASHBOARD_ROUTES,
    ...TIER_1_DASHBOARD_ROUTES,
    ...TIER_2_DASHBOARD_ROUTES,
  ] as const;

export type TrueRoutePathParams<Path extends SUPPORTED_ROUTES> =
  RouteComponentProps<{ [K in keyof ExtractRouteParams<Path>]: string }>;

type RoutePathParams = Record<RouteParameters, string>;

export type { RoutePathParams };

export type RouterPathParams = RouteComponentProps<Partial<RoutePathParams>>;

type OrganizationRouteParams = Pick<
  RoutePathParams,
  RouteParameters.ORGANIZATION_ID
>;

function getOrganizationRouteParams(
  organizationId: number,
): OrganizationRouteParams {
  return { [RouteParameters.ORGANIZATION_ID]: String(organizationId) };
}

export function isDashboardRoute(
  path: string | (typeof ACTIVE_DASHBOARD_ROUTES)[number],
): path is SUPPORTED_ROUTES {
  if (
    typeof path === "string" &&
    ACTIVE_DASHBOARD_ROUTES.includes(
      path as (typeof ACTIVE_DASHBOARD_ROUTES)[number],
    )
  ) {
    return true;
  }

  return false;
}

export function isRouteParameter(path: string): path is RouteParameters {
  if (RouteParametersArray.includes(path.slice(1) as RouteParameters)) {
    return true;
  }
  return false;
}

export function getRoutePath<Path extends SUPPORTED_ROUTES>(
  path: Path,
  params: ExtractRouteParams<Path>,
  queryParams?: Path extends keyof PATH_TO_QUERY_PARAMS
    ? PATH_TO_QUERY_PARAMS[Path]
    : Record<string, string>,
): Path {
  const url = generatePath(path, params);
  if (queryParams && Object.keys(queryParams).length > 0) {
    return `${url}?${new URLSearchParams(queryParams).toString()}` as Path;
  }
  return url as Path;
}

function getAdministrationPath(
  subpath: string = "",
  queryParams?: PATH_TO_QUERY_PARAMS[typeof ADMINISTRATION_ROUTING.AIRPLANES],
): string {
  const path = subpath ? `${ADMINISTRATION_ROUTING.ROOT}${subpath}` : ADMINISTRATION_ROUTING.ROOT;
  if (queryParams && Object.keys(queryParams).length > 0) {
    return `${path}?${new URLSearchParams(queryParams as Record<string, string>).toString()}`;
  }
  return path;
}

function getOrganizationManagementPath(
  organizationId: number,
  queryParams?: PATH_TO_QUERY_PARAMS[typeof ROUTING.DASHBOARD.ORGANIZATION.ORGANIZATION_MANAGEMENT.ROOT],
): string {
  return getAdministrationPath(
    "",
    queryParams as PATH_TO_QUERY_PARAMS[typeof ADMINISTRATION_ROUTING.AIRPLANES] | undefined,
  );
}

function getOrganizationCheckoutPath(
  organizationId: number,
  checkoutId: string,
): string {
  return getRoutePath(
    ROUTING.DASHBOARD.ADMINISTRATION.CHECKOUT[RouteParameters.CHECKOUT_ID].ROOT,
    {
      [RouteParameters.CHECKOUT_ID]: checkoutId,
    },
  );
}

function isOrganizationScopedRoute(path: SUPPORTED_ROUTES): boolean {
  return path.includes(`:${RouteParameters.ORGANIZATION_ID}`);
}

function getRouteLevel(path: SUPPORTED_ROUTES): FeatureAccessLevel {
  if (
    FREE_DASHBOARD_ROUTES.includes(
      path as (typeof FREE_DASHBOARD_ROUTES)[number],
    )
  ) {
    return FeatureAccessLevel.FREE;
  } else if (
    TIER_1_DASHBOARD_ROUTES.includes(
      path as (typeof TIER_1_DASHBOARD_ROUTES)[number],
    )
  ) {
    return FeatureAccessLevel.TIER_1;
  } else if (
    TIER_2_DASHBOARD_ROUTES.includes(
      path as (typeof TIER_2_DASHBOARD_ROUTES)[number],
    )
  ) {
    return FeatureAccessLevel.TIER_2;
  }
  return FeatureAccessLevel.FREE;
}

export {
  ACTIVE_DASHBOARD_ROUTES,
  getAdministrationPath,
  getOrganizationCheckoutPath,
  getOrganizationManagementPath,
  getOrganizationRouteParams,
  getRouteLevel,
  isOrganizationScopedRoute,
  TIER_1_DASHBOARD_ROUTES,
  TIER_2_DASHBOARD_ROUTES,
};
